<template>
  <v-row no-gutters class="fill-height flex-column pr-5 pb-5">
    <card-title-row class="pl-0 pt-5" translation-path="sidebar.waterBalanceDma.title" :has-buttons="false" />
    <water-balance-wizard
      ref="dma-wizard"
      tool-name="dma"
      :inputs="stepsInputs"
      :steps="steps"
      :params-proxy.sync="params"
      with-profiles
      :profiles-data="profilesData"
    >
    </water-balance-wizard>
  </v-row>
</template>

<script>
import WaterBalanceWizard from '@/components/WaterBalanceWizard';
import CardTitleRow from '@/components/CardTitleRow';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { get, call } from 'vuex-pathify';

export default {
  name: 'WaterBalanceDma',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    WaterBalanceWizard,
    CardTitleRow,
  },
  data: () => ({
    params: {},
  }),
  methods: {
    ...call('waterBalance/*DmaProfil*'),
  },
  computed: {
    projectElements: get('layers/project@layers'),
    dmaProfiles: get('waterBalance/dmaProfiles'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    valvesLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_valves');
    },
    pipesLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_pipes');
    },
    waterPointLayers() {
      return this.projectLayers.filter(
        layer => layer.layer_scope === 'water' && ['point', 'multipoint'].includes(layer.geometry_type)
      );
    },
    pointLayers() {
      return this.projectLayers.filter(layer => ['point', 'multipoint'].includes(layer.geometry_type));
    },
    defaultInputProps() {
      return {
        dataType: { name: 'select' },
        rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        cols: '7',
        nameCols: '5',
      };
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.waterBalanceDma.valves',
        },
        {
          step: 2,
          translationPath: 'sidebar.waterBalanceDma.flowmeters',
        },
        {
          step: 3,
          translationPath: 'sidebar.waterBalanceDma.pipes',
        },
        {
          step: 4,
          translationPath: 'sidebar.waterBalanceDma.dmaAlerts',
        },
        {
          step: 5,
          translationPath: 'sidebar.waterBalanceDma.analysis',
        },
      ];
    },
    profilesData() {
      return {
        translationPaths: {
          addProfileDialogTitle: 'dialog.title.profiles.waterBalanceDmaAdd',
          editProfileDialogTitle: 'dialog.title.profiles.waterBalanceDmaEdit',
          deleteProfileDialogTitle: 'dialog.title.profiles.waterBalanceDmaDelete',
        },
        profiles: this.dmaProfiles,
        getProfiles: this.getDmaProfiles,
        saveProfile: this.saveDmaProfile,
        editProfile: this.editDmaProfile,
        deleteProfile: this.deleteDmaProfile,
      };
    },
    stepsInputs() {
      return [
        {
          name: 'valve_layer',
          isLayer: true,
          step: 1,
          translationPath: 'sidebar.waterBalanceDma.valvesLayer',
          items: this.valvesLayers,
          itemText: 'name',
          disabled: this.valvesLayers?.length === 0,
          tooltip: this.valvesLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceDma.addValvesToRun') : null,
          itemValue: 'layer_id',
          ...this.defaultInputProps,
        },
        {
          name: 'valve_zone',
          step: 1,
          connectedValue: ['valve_zone_value'],
          connectedLayer: 'valve_layer',
          translationPath: 'sidebar.waterBalanceDma.valvesZoningAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['valve_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'valve_zone_value',
          step: 1,
          connectedAttribute: 'valve_zone',
          translationPath: 'sidebar.waterBalanceDma.valvesZoningValue',
          disabled: !this.params['valve_layer'] || !this.params['valve_zone'],
          ...this.defaultInputProps,
        },
        {
          name: 'valve_fixed_status',
          step: 1,
          connectedValue: ['valve_closed_value'],
          connectedLayer: 'valve_layer',
          translationPath: 'sidebar.waterBalanceDma.valvesStateAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['valve_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'valve_closed_value',
          step: 1,
          connectedAttribute: 'valve_fixed_status',
          translationPath: 'sidebar.waterBalanceDma.valvesClosedValue',
          disabled: !this.params['valve_layer'] || !this.params['valve_fixed_status'],
          ...this.defaultInputProps,
        },
        {
          name: 'valve_pipe',
          step: 1,
          connectedLayer: 'valve_layer',
          translationPath: 'sidebar.waterBalanceDma.pipesIdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['valve_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_layer',
          step: 2,
          isLayer: true,
          translationPath: 'sidebar.waterBalanceDma.flowmetersLayer',
          items: this.waterPointLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.waterPointLayers?.length === 0,
          tooltip:
            this.waterPointLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceDma.addFlowmetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_analyze',
          step: 2,
          connectedValue: ['flow_meter_analyze_value'],
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceDma.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_analyze_value',
          step: 2,
          connectedAttribute: 'flow_meter_analyze',
          translationPath: 'sidebar.waterBalanceDma.analysisValue',
          disabled: !this.params['flow_meter_layer'] || !this.params['flow_meter_analyze'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_zone',
          step: 2,
          connectedValue: ['flow_meter_zone_value'],
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceDma.flowmetersZoneSplitAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_zone_value',
          step: 2,
          connectedAttribute: 'flow_meter_zone',
          translationPath: 'sidebar.waterBalanceDma.flowmetersZoneSplitValue',
          disabled: !this.params['flow_meter_layer'] || !this.params['flow_meter_zone'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_name',
          step: 2,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceDma.flowmetersNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_pipe',
          step: 2,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceDma.pipesIdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_layer',
          step: 3,
          isLayer: true,
          translationPath: 'sidebar.waterBalanceDma.pipesLayer',
          items: this.pipesLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.pipesLayers?.length === 0,
          tooltip: this.pipesLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceDma.addPipesToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_analyze',
          step: 3,
          connectedValue: 'pipes_analyze_values',
          connectedLayer: 'pipes_layer',
          translationPath: 'sidebar.waterBalanceDma.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['pipes_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_analyze_values',
          step: 3,
          connectedAttribute: 'pipes_analyze',
          translationPath: 'sidebar.waterBalanceDma.analysisValues',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['pipes_layer'] || !this.params['pipes_analyze'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_name_layer',
          isLayer: true,
          step: 4,
          translationPath: 'sidebar.waterBalanceDma.dmaNamesLayer',
          items: this.pointLayers,
          itemText: 'name',
          disabled: this.pointLayers?.length === 0,
          tooltip: this.pointLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceDma.addPointsToRun') : null,
          itemValue: 'layer_id',
          ...this.defaultInputProps,
        },
        {
          name: 'dma_name_description',
          step: 4,
          connectedLayer: 'dma_name_layer',
          translationPath: 'sidebar.waterBalanceDma.dmaNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          allowedAttributeTypes: ['integer', 'float', 'decimal', 'text'],
          disabled: !this.params['dma_name_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_name_alarm_avg_inflow_threshold',
          step: 4,
          connectedLayer: 'dma_name_layer',
          translationPath: 'sidebar.waterBalanceDma.alarmAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          allowedAttributeTypes: ['integer', 'float', 'decimal'],
          disabled: !this.params['dma_name_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_name_alarm_max_inflow_threshold',
          step: 4,
          connectedLayer: 'dma_name_layer',
          translationPath: 'sidebar.waterBalanceDma.alarmMaxThresholdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          allowedAttributeTypes: ['integer', 'float', 'decimal'],
          disabled: !this.params['dma_name_layer'],
          ...this.defaultInputProps,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-stepper--alt-labels .v-stepper__header .v-stepper__step {
    min-width: 90px !important;
  }
}
</style>
